import { useAsyncData, useRoute, useState } from '#imports'
import { computed, ref, shallowReactive, watch } from 'vue'

import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import type { SearchConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useTestChallenge } from '@backmarket/nuxt-layer-algolia/useRedirectToTestChallenge'
import { buildUiFilters } from '@backmarket/nuxt-layer-recommendation/filtering/buildUiFilters'
import { filtersToWhereClause } from '@backmarket/nuxt-layer-recommendation/filtering/filtersToWhereClause'
import { getGraphFacet } from '@backmarket/nuxt-layer-recommendation/filtering/getGraphFacet'
import { getInitialFiltersDict } from '@backmarket/nuxt-layer-recommendation/filtering/getInitialFiltersDict'
import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import {
  algoliaHitToProduct,
  algoliaHitToVariant,
} from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import algoliasearch from 'algoliasearch'

export interface SearchTrackingData {
  id?: string
  index?: string
  productObjectId?: string
}

const DEBOUNCE_DELAY = 500

export async function useProductsSearch(
  configuration: SearchConfiguration,
  initialQueryValue = '',
  displayHero = false,
) {
  const logger = useLogger()
  const { redirectToTestChallengePage, isTestChallengeError } =
    useTestChallenge()

  const products = ref<Product[]>([])
  const heroProduct = ref<Product>()
  const total = ref(0)
  const facets = ref<Facet[]>([])
  const sortInput = ref(configuration.indexes.active.name)

  const getInitialPage = () => {
    try {
      const page = useRoute().query.p?.toString() || '1'

      return Number.parseInt(page, 10)
    } catch (e) {
      return 1
    }
  }
  const pageInput = ref(getInitialPage())
  const pageCount = ref(0)
  const priceInput = ref<[number, number]>([0, 0])
  const priceFacet = ref<UiPriceFacet>()
  const query = ref(initialQueryValue)
  const resettingFilters = ref(false)

  const index = computed(() => {
    return (
      [
        configuration.indexes.active,
        ...(configuration.indexes?.other ?? []),
      ].find(({ name }) => name === sortInput.value) ??
      configuration.indexes.active
    )
  })

  const searchTrackingData = useState<SearchTrackingData>(
    'search-query',
    () => ({
      id: '',
      index: '',
    }),
  )

  const filtersInput = shallowReactive(
    getInitialFiltersDict(configuration.facets),
  )

  const isPriceFiltered = computed(
    () =>
      priceInput.value[0] > 0 || priceInput.value[1] !== priceFacet.value?.max,
  )

  const isFiltered = computed(() => {
    return (
      isPriceFiltered.value ||
      Object.values(filtersInput).reduce((acc, filter) => {
        if (filter.length > 0) {
          return true
        }

        return acc
      }, false)
    )
  })

  const facetCount = computed(() => {
    const numberOfFiltersActive = Object.values(filtersInput).filter(
      (filterValue) => filterValue.length > 0,
    ).length

    return numberOfFiltersActive + (isPriceFiltered.value ? 1 : 0)
  })

  const algoliaSearchOption = useAlgoliaSearchOptions('search')

  const algoliaClient = algoliasearch('dummy_app_id', 'dummy_key', {
    ...algoliaSearchOption,
  })

  function resetPriceFilter() {
    priceInput.value[0] = 0
    priceInput.value[1] = priceFacet.value?.max ?? 0
  }

  function resetFilters() {
    for (const [key] of Object.entries(filtersInput)) {
      filtersInput[key] = []
    }
  }

  function resetAllFilters() {
    resettingFilters.value = true
    priceFacet.value = undefined
    facets.value = []
    resetPriceFilter()
    resetFilters()
    resettingFilters.value = false
  }

  watch(
    [filtersInput, sortInput, pageInput],
    () => {
      if (!resettingFilters.value) void search()
    },
    { deep: true },
  )

  watch(
    () => query.value,
    () => {
      resetAllFilters()
      void search()
    },
  )

  const debouncedSearch = useDebounceFn(search, DEBOUNCE_DELAY)

  watch(priceInput, debouncedSearch, { deep: true })

  async function search() {
    const defaultFacets = ['price', 'page', 'q', 'sort']
    const sidebarFacets = configuration.facets.map(({ name }) => name)
    const { features } = useMarketplace()

    const hitsPerPage = features.buyback?.tradeInEnabled ? 31 : 32

    const priceFacets =
      configuration.priceFacet?.scales.map((facet) => facet.facetName) ?? []
    const requestOptions = {
      ...index.value.queryParams,
      filters: filtersToWhereClause(filtersInput, configuration.complexFilter),
      facets: [...defaultFacets, ...sidebarFacets, ...priceFacets],
      numericFilters:
        priceInput.value[1] > 0
          ? [`price>=${priceInput.value[0]}`, `price<=${priceInput.value[1]}`]
          : undefined,
      page: pageInput.value - 1,
      hitsPerPage,
    }

    const algoliaIndex = algoliaClient.initIndex(index.value.name)

    try {
      const { data: algoliaResult } = await useAsyncData(
        getAsyncDataKey('algolia-search', {
          index: index.value.name,
          query: query.value,
          page: pageInput.value,
          sort: sortInput.value,
          filters: filtersInput,
          priceMin: priceInput.value[0],
          priceMax: priceInput.value[1],
        }),
        () => algoliaIndex.search<AlgoliaProduct>(query.value, requestOptions),
      )

      if (!algoliaResult.value) return

      const {
        nbHits,
        facets_stats: algoliaFacetsStats,
        hits,
        queryID,
        nbPages,
        facets: algoliaFacets,
        processingTimeMS,
      } = algoliaResult.value

      const algoliaPriceValues = {
        avg: algoliaFacetsStats?.price.avg ?? 0,
        max: algoliaFacetsStats?.price.max ?? 0,
      }

      searchTrackingData.value = {
        id: queryID,
        index: index.value.name,
      }

      total.value = nbHits
      if (configuration.productGroupingType === 'parent') {
        products.value = hits.map((hit) => algoliaHitToProduct(hit))
      } else if (configuration.productGroupingType === 'variant') {
        products.value = hits.map((hit) => algoliaHitToVariant(hit))
      }
      pageCount.value = nbPages
      facets.value = buildUiFilters(
        facets.value,
        configuration.facets,
        algoliaFacets,
      )

      if (
        !priceFacet.value &&
        configuration.priceFacet &&
        algoliaFacets &&
        algoliaFacetsStats
      ) {
        priceFacet.value = getGraphFacet(
          configuration.priceFacet,
          algoliaFacets,
          algoliaPriceValues,
        )

        priceInput.value = [0, priceFacet.value.max]
      }
      logger.info('[S&R] PLP search query success', {
        endpointName: 'algoliaSearch',
        type: 'API_REQUEST_SUCCESS',
        searchProcessingTime: processingTimeMS,
        owners: ['bot-squad-search-and-recommendation-front'],
      })

      if (nbHits === 0) {
        logger.info('[S&R] PLP returned empty results', {
          type: 'EMPTY_SEARCH_RESULTS',
          owners: ['bot-squad-search-and-recommendation-front'],
        })
      }
    } catch (e) {
      logger.error('[S&R] PLP search query failed', {
        endpointName: 'algoliaSearch',
        type: 'API_REQUEST_FAIL',
        e,
        owners: ['bot-squad-search-and-recommendation-front'],
      })

      if (isTestChallengeError(e)) {
        redirectToTestChallengePage()
      }
    }
  }

  async function searchHero() {
    if (!displayHero) return
    const requestOptions = {
      ...index.value.queryParams,
      filters: filtersToWhereClause(filtersInput, configuration.complexFilter),
      page: 0,
      hitsPerPage: 1,
    }

    const algoliaIndex = algoliaClient.initIndex(index.value.name)

    try {
      const { data: algoliaResult } = await useAsyncData(
        getAsyncDataKey('algolia-hero-search', {
          index: index.value.name,
          query: query.value,
          filters: filtersInput,
        }),
        () => algoliaIndex.search<AlgoliaProduct>(query.value, requestOptions),
      )

      if (!algoliaResult.value) return

      const { hits, processingTimeMS } = algoliaResult.value

      heroProduct.value = hits.map((hit) => algoliaHitToProduct(hit))[0]

      logger.info('[S&R] PLP hero search query success', {
        endpointName: 'algoliaSearch',
        type: 'API_REQUEST_SUCCESS',
        searchProcessingTime: processingTimeMS,
        owners: ['bot-squad-search-and-recommendation-front'],
      })
    } catch (e) {
      logger.error('[S&R] PLP hero search query failed', {
        endpointName: 'algoliaSearch',
        type: 'API_REQUEST_FAIL',
        e,
        owners: ['bot-squad-search-and-recommendation-front'],
      })
    }
  }

  await Promise.all([search(), searchHero()])

  return {
    total,
    pageCount,
    products,
    heroProduct,
    facets,
    priceFacet,
    facetCount,
    filtersInput,
    sortInput,
    pageInput,
    priceInput,
    query,
    resettingFilters,
    search,
    isFiltered,
  }
}
